import type {
  SubscriptionEventType,
  UserFilter,
  UserSort,
} from './types';

/**
 * The Users page keeps its whole query — app, filter, sort — in the URL, so a
 * dashboard tile can link straight to "who subscribed today" and the result
 * survives a reload or a shared link.
 */

const BOOLEAN_KEYS = [
  'is_premium',
  'is_trial',
  'is_gift',
  'is_active',
  'has_fcm_token',
  'has_named_account',
  'has_connected_account',
] as const satisfies readonly (keyof UserFilter)[];

const STRING_KEYS = [
  'app_key',
  'app_package_name',
  'platform',
  'language',
  'country',
  'current_app_version',
  'registered_from',
  'registered_to',
  'last_seen_from',
  'subscription_event',
  'subscription_event_from',
  'search',
  'ip_address',
] as const satisfies readonly (keyof UserFilter)[];

const SORTS: readonly UserSort[] = ['newest', 'last_seen', 'sessions'];

/** `/users` pre-filtered to [filter], read in [sort] order. */
export function usersLink(filter: UserFilter, sort?: UserSort): string {
  const params = writeUsersParams(filter, sort);
  const query = params.toString();
  return query ? `/users?${query}` : '/users';
}

export function writeUsersParams(
  filter: UserFilter,
  sort?: UserSort,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  if (sort && sort !== 'newest') params.set('sort', sort);
  return params;
}

/** The inverse of {@link writeUsersParams}; unknown keys are ignored. */
export function readUsersParams(params: URLSearchParams): {
  filter: UserFilter;
  sort: UserSort;
} {
  const filter: Record<string, string | boolean> = {};
  for (const key of STRING_KEYS) {
    const value = params.get(key);
    if (value) filter[key] = value;
  }
  for (const key of BOOLEAN_KEYS) {
    const value = params.get(key);
    if (value === 'true' || value === 'false') filter[key] = value === 'true';
  }

  const sort = params.get('sort') as UserSort | null;
  return {
    filter,
    sort: sort && SORTS.includes(sort) ? sort : 'newest',
  };
}

/** Shorthand for the dashboard's subscription-event drill-downs. */
export function subscriptionEventLink(
  type: SubscriptionEventType,
  from: string,
): string {
  return usersLink({
    subscription_event: type,
    subscription_event_from: from,
  });
}
